"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { ArrowRight, MonitorSmartphone, Bot, Network, Building2, Zap, ShieldCheck, Phone, Headphones, Server, MessageSquareText } from "lucide-react";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function MagneticWrapper({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const handleMouse = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const {
      clientX,
      clientY
    } = e;
    const {
      height,
      width,
      left,
      top
    } = ref.current.getBoundingClientRect();
    const middleX = clientX - (left + width / 2);
    const middleY = clientY - (top + height / 2);
    setPosition({
      x: middleX * 0.25,
      y: middleY * 0.25
    });
  };
  const reset = () => {
    setPosition({
      x: 0,
      y: 0
    });
  };
  return <div ref={ref} onMouseMove={handleMouse} onMouseLeave={reset} className={cn("inline-block transition-transform duration-200 ease-out", className)} style={{
    transform: `translate(${position.x}px, ${position.y}px)`
  }}>
      {children}
    </div>;
}
const services = [{
  title: "Integración MS Teams",
  description: "Conecte su infraestructura de telefonía directamente con Microsoft Teams para una colaboración unificada y eficiente.",
  icon: MonitorSmartphone,
  to: "/MsTeamsIntegrationPage",
  delay: 0
}, {
  title: "Agentes de IA",
  description: "Automatice la atención al cliente con agentes inteligentes capaces de resolver consultas complejas en tiempo real.",
  icon: Bot,
  to: "/ServiciosIAPage",
  delay: 100
}, {
  title: "Centrales PBX-Vtec / Omnicanal",
  description: "Soluciones de telefonía IP y omnicanalidad robustas y escalables para optimizar la atención de su empresa.",
  icon: Network,
  to: "/OmnichannelPage",
  delay: 200
}, {
  title: "Centrales Grandstream",
  description: "Equipos de comunicación IP de alto rendimiento con tecnología de vanguardia para empresas en crecimiento.",
  icon: Building2,
  to: "/GrandstreamCentralPage",
  delay: 300
}, {
  title: "Centrales 3CX",
  description: "Sistema de comunicaciones de software abierto que ofrece flexibilidad total y reducción de costos operativos.",
  icon: Zap,
  to: "/Central3CXPage",
  delay: 400
}, {
  title: "SBC Seguridad de Voz",
  description: "Session Border Controllers para proteger y encriptar su red de voz IP contra ataques y fraudes.",
  icon: ShieldCheck,
  to: "/SbcSeguridadDeVozPage",
  delay: 500
}, {
  title: "Productos Grandstream",
  description: "Teléfonos IP, gateways y dispositivos de videoconferencia de alta calidad para cualquier entorno laboral.",
  icon: Phone,
  to: "/GrandstreamProductsPage",
  delay: 600
}, {
  title: "Producto Yealink",
  description: "Terminales y accesorios premium Yealink diseñados para una comunicación cristalina y colaborativa.",
  icon: Headphones,
  to: "/YealinkProductPage",
  delay: 700
}, {
  title: "Productos Dinstar",
  description: "Gateways VoIP, intercomunicadores y soluciones de acceso para modernizar la telefonía empresarial.",
  icon: Server,
  to: "/DinstarProductsPage",
  delay: 800
}, {
  title: "Transcripción y Análisis de Chats y Llamadas",
  description: "IA avanzada para transcribir, auditar y extraer inteligencia conversacional de sus llamadas y mensajes.",
  icon: MessageSquareText,
  to: "/TranscriptionAndAnalysisOfChatsAndCallsPage",
  delay: 900
}];
function ServiceCard({
  service,
  index
}: {
  service: (typeof services)[number];
  index: number;
}) {
  const {
    ref: itemRef,
    isVisible: isItemVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const ServiceIcon = service.icon;
  return <div ref={itemRef} data-index={index} className="h-full">
      <Link to={service.to} className="block h-full text-inherit no-underline">
        <div className={cn("group relative h-full p-6 lg:p-8", "flex flex-col items-center text-center", "rounded-md border border-border/60", "bg-accent/20 backdrop-blur-sm", "cursor-pointer", "hover:bg-accent hover:scale-[1.02] hover:shadow-xl", "transition-all duration-500")}>
          <div className={cn("mb-6 p-5 rounded-full", "bg-primary text-primary-foreground", "transition-all duration-700 ease-out", "group-hover:animate-pulse", isItemVisible ? "opacity-100 scale-100" : "opacity-0 scale-90")} style={{
          transitionDelay: `${service.delay}ms`
        }}>
            <ServiceIcon className={cn("w-8 h-8 draw-icon", "transition-transform duration-500", "group-hover:scale-110", isItemVisible && "is-visible")} strokeWidth={1.5} />
          </div>

          <div className={cn("flex-grow flex flex-col", "transition-all duration-700 ease-out", isItemVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")} style={{
          transitionDelay: `${service.delay + 100}ms`
        }}>
            <h3 className="text-xl font-bold font-default mb-3 text-foreground">
              {service.title}
            </h3>

            <p className="text-sm text-foreground/90 font-default mb-8 leading-relaxed">
              {service.description}
            </p>

            <div className="mt-auto pt-2">
              <MagneticWrapper>
                <span className="inline-flex items-center gap-2 text-sm font-bold font-default text-foreground group-hover:text-primary transition-colors">
                  <span className="relative overflow-hidden">
                    <span className="inline-block transition-transform duration-300 group-hover:-translate-y-full">
                      Saber Más
                    </span>

                    <span className="inline-block absolute left-0 top-0 translate-y-full transition-transform duration-300 group-hover:translate-y-0 text-primary">
                      Saber Más
                    </span>
                  </span>

                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </MagneticWrapper>
            </div>
          </div>
        </div>
      </Link>
    </div>;
}
export default function EcosistemaDeSoluciones() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="221620" id="ecosistema-de-soluciones" className="py-10 bg-background text-foreground relative overflow-hidden rounded-none !opacity-[1] lg:py-14">
      <style>
        {`
          .draw-icon path,
          .draw-icon circle,
          .draw-icon rect,
          .draw-icon line,
          .draw-icon polyline,
          .draw-icon polygon {
            stroke-dasharray: 1000;
            stroke-dashoffset: 1000;
          }

          .draw-icon.is-visible path,
          .draw-icon.is-visible circle,
          .draw-icon.is-visible rect,
          .draw-icon.is-visible line,
          .draw-icon.is-visible polyline,
          .draw-icon.is-visible polygon {
            animation: draw-svg 2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          }

          @keyframes draw-svg {
            0% {
              stroke-dashoffset: 1000;
            }

            100% {
              stroke-dashoffset: 0;
            }
          }
        `}
      </style>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={headerRef} className={cn("mb-16 md:mb-24 max-w-2xl transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-sm font-default font-bold tracking-widest uppercase text-primary mb-4">
            Ecosistema de Soluciones
          </h2>

          <p className="text-3xl md:text-4xl lg:text-5xl font-default font-bold leading-tight tracking-tight text-primary">
            Elevando la conectividad digital mediante diseño estratégico y
            excelencia técnica.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 items-stretch">
          {services.map((service, index) => <ServiceCard key={service.to} service={service} index={index} />)}
        </div>
      </div>
    </section>;
}